import { openDB, IDBPDatabase, DBSchema, StoreNames } from 'idb';
import { ExecutedActiveHelpScenario } from '../models/ExecutedActiveHelpScenario';
import { Interaction } from '../models/customer-session/Interaction';
import { CustomerSession } from '../models/customer-session/CustomerSession';
interface BrilliantDBSchema extends DBSchema {
    interactions: {
        key: number;
        value: Interaction;
        indexes: {
            'by-date': Date;
        };
    };
    executedScenarios: {
        key: number;
        value: ExecutedActiveHelpScenario;
        indexes: {
            'by-hash': string;
            'by-date': Date;
        };
    };
    sessions: {
        key: string;
        value: CustomerSession;
    };
}

let db: IDBPDatabase<BrilliantDBSchema>;

export async function initializeIndexedDbService(): Promise<void> {
    db = await openDB<BrilliantDBSchema>('bri-active-help', 4, {
        upgrade(db) {
            if (!db.objectStoreNames.contains('interactions')) {
                const interactionsStore = db.createObjectStore('interactions', {
                    autoIncrement: true
                });
                interactionsStore.createIndex('by-date', 'date');
            }
            if (!db.objectStoreNames.contains('executedScenarios')) {
                const executedScenariosStore = db.createObjectStore(
                    'executedScenarios',
                    {
                        autoIncrement: true
                    }
                );
                executedScenariosStore.createIndex('by-hash', 'hash');
                executedScenariosStore.createIndex('by-date', 'date');
            }
            if (!db.objectStoreNames.contains('sessions')) {
                db.createObjectStore('sessions', {
                    keyPath: 'id'
                });
            }
        }
    });
}

export function getDb(): IDBPDatabase<BrilliantDBSchema> {
    return db;
}

export async function getLast<T extends StoreNames<BrilliantDBSchema>>(
    storeName: T
): Promise<{
    key: BrilliantDBSchema[T]['key'];
    value: BrilliantDBSchema[T]['value'];
} | null> {
    const cursor = await getDb()
        .transaction(storeName, 'readonly')
        .store.openCursor(null, 'prev');
    if (!cursor) {
        return null;
    }
    return {
        key: cursor.primaryKey,
        value: cursor.value
    };
}

export async function updateOne<
    T extends StoreNames<BrilliantDBSchema>,
    U extends BrilliantDBSchema[T]['value']
>(
    storeName: T,
    key: BrilliantDBSchema[T]['key'],
    updateFn: (interaction: U) => U,
    options: {
        useInLineKeysToUpdate?: boolean;
    } = {}
): Promise<U> {
    // NOTE: Important: Do not await other things between the start and end of your transaction, otherwise the transaction will close before you're done.
    // see: https://www.npmjs.com/package/idb#transaction-lifetime
    const tx = getDb().transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const val = await store.get(key);
    if (!val) {
        throw new Error(
            `Update transaction failed due to key not found in "${storeName}" store. Key: ${key}`
        );
    }
    const updated = updateFn(val as U);
    await store.put(updated, options.useInLineKeysToUpdate ? undefined : key);
    await tx.done;
    return updated;
}
